// External Libraries
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { CreatableMultiSelect } from "sj-ui-toolkit/src/components/CreatableMultiSelect/CreatableMultiSelect";

// Local Components
import S from "./ProfileDetailsTab.module.scss";

// Constants
import { FIELD_TYPES } from "../../../constants/profileDetailsFields";
import type { FieldDefinition } from "../../../constants/profileDetailsFields";
import type { ProfileFormData } from "../../../helpers/userProfile";

interface ProfileFieldProps {
  field: FieldDefinition & { locked?: boolean; disabled?: boolean };
  formData: ProfileFormData;
  isEditing: boolean;
  handleChange: (field: string, value: unknown) => void;
}

export function ProfileField({ field, formData, isEditing, handleChange }: ProfileFieldProps) {
  const { label, key, type, options, locked, disabled } = field;
  const value = formData[key] as string | string[] | undefined;
  const otherKey = `${key}Other`;
  const otherValue = formData[otherKey] as string | undefined;

  const isOtherSelected = () => {
    if (type === FIELD_TYPES.DROPDOWN) return value === "other";
    if (type === FIELD_TYPES.MULTISELECT) return ((value as string[]) || []).includes("other");
    return false;
  };

  const hasValidOtherValue = () => (otherValue?.trim().length ?? 0) > 0;

  const shouldShowOtherInput = field.hasOtherInput && isOtherSelected();
  const isOtherInputInvalid = shouldShowOtherInput && !hasValidOtherValue();

  const renderEditableField = () => {
    if (type === FIELD_TYPES.TEXT) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">{value as string}</span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <InputText
            id={key}
            className="w-full"
            value={(value as string) || ""}
            onChange={e => handleChange(key, e.target.value)}
            data-testid={`profile-field-${key}-input`}
          />
        </div>
      );
    }

    if (type === FIELD_TYPES.DROPDOWN) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">
            {options?.find(opt => opt.value === value)?.label || (value as string)}
          </span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <Dropdown
            inputId={key}
            className="w-full"
            value={value as string}
            options={options || []}
            onChange={e => handleChange(key, e.value)}
            placeholder={`Select ${label.toLowerCase()}`}
            disabled={disabled}
            pt={{
              select: { "aria-label": `Select ${label}` },
            }}
            data-testid={`profile-field-${key}-dropdown`}
          />

          {shouldShowOtherInput && (
            <div className="mt-2">
              <InputText
                id={otherKey}
                className="w-full"
                value={otherValue || ""}
                onChange={e => handleChange(otherKey, e.target.value)}
                placeholder={`Please specify your ${label.toLowerCase()}`}
                data-testid={`profile-field-${key}-other-input`}
              />
              {isOtherInputInvalid && <small className="text-error">This field is required</small>}
            </div>
          )}
        </div>
      );
    }

    if (type === FIELD_TYPES.CREATABLE_MULTISELECT) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">
            {((value as string[]) || [])
              .map(v => options?.find(opt => opt.value === v)?.label || v)
              .join(", ")}
          </span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <CreatableMultiSelect
            inputId={key}
            className="w-full"
            value={(value as string[]) || []}
            options={options || []}
            onChange={e => handleChange(key, e.value)}
            placeholder={`Select ${label.toLowerCase()}`}
            disabled={disabled}
            display="chip"
            data-testid={`profile-field-${key}-select`}
          />
        </div>
      );
    }

    if (type === FIELD_TYPES.MULTISELECT) {
      return locked ? (
        <div className={S.profileDetailText}>
          <i className="pi pi-lock" title="This field is locked and cannot be edited" />
          <span className="ml-2">
            {((value as string[]) || [])
              .map(v => options?.find(opt => opt.value === v)?.label || v)
              .join(", ")}
          </span>
        </div>
      ) : (
        <div className={S.profileInput}>
          <MultiSelect
            inputId={key}
            className="w-full"
            value={((value as string[]) || []).map(v => v.toLowerCase())}
            options={options || []}
            onChange={e => handleChange(key, e.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            disabled={disabled}
            data-testid={`profile-field-${key}-multiselect`}
          />

          {shouldShowOtherInput && (
            <div className="mt-2">
              <InputText
                id={otherKey}
                className="w-full"
                value={otherValue || ""}
                onChange={e => handleChange(otherKey, e.target.value)}
                placeholder={`Please specify your ${label.toLowerCase()}`}
                data-testid={`profile-field-${key}-other-input`}
              />
              {isOtherInputInvalid && <small className="text-error">This field is required</small>}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderReadOnlyField = () => {
    // --- DROPDOWN "Other" ---
    if (type === FIELD_TYPES.DROPDOWN && value === "other") {
      const otherVal = formData[`${key}Other`] as string | undefined;
      return <div className={S.profileDetailText}>{otherVal || "-"}</div>;
    }

    // --- MULTISELECT (Tooling) ---
    if (Array.isArray(value)) {
      const items = (value as string[]).map(v => {
        if (v === "other") {
          return (formData[`${key}Other`] as string | undefined) || "other";
        }
        return options?.find(opt => opt.value === v)?.label || v;
      });

      return <div className={S.profileDetailText}>{items.join(", ") || "-"}</div>;
    }

    // --- DEFAULT ---
    return (
      <div className={S.profileDetailText}>
        {options?.find(opt => opt.value === (value as string))?.label || (value as string) || "-"}
      </div>
    );
  };

  return (
    <div>
      {isEditing && !locked ? (
        <label htmlFor={key} className="text-muted-aaa body-sm">
          {label}
        </label>
      ) : (
        <div className="text-muted-aaa body-sm">{label}</div>
      )}
      {isEditing ? renderEditableField() : renderReadOnlyField()}
    </div>
  );
}
